import React, { useMemo, useState } from 'react';
import { deleteAlignmentScan } from '../../api/client';
import { Sensor } from '../../api/models';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { invalidateBinPointCloudCache } from '../../utils/loadBinPointCloud';
import {
  categorizeSensorPcd,
  formatComparisonVerdict,
  formatPcdTimestamp,
  pcdCategoryLabel,
  PcdCategory,
  sensorPcdTimestamp,
} from '../../utils/pcdStatus';

interface PcdStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.75)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const panelStyle: React.CSSProperties = {
  background: '#1e293b',
  color: 'white',
  borderRadius: '8px',
  border: '1px solid #334155',
  padding: '20px',
  maxWidth: '760px',
  width: '94%',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
};

const scrollBodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
  marginBottom: '12px',
};

const primaryButtonStyle: React.CSSProperties = {
  background: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  padding: '8px 12px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 500,
};

const secondaryButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: '#334155',
};

const dangerButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  background: '#7f1d1d',
  padding: '4px 8px',
  fontSize: '12px',
};

const statusColor = (status?: string) => {
  if (status === 'aligned') return '#10b981';
  if (status === 'unaligned') return '#f59e0b';
  if (status === 'none' || !status) return '#94a3b8';
  return '#cbd5e1';
};

const categoryColor: Record<PcdCategory, string> = {
  new: '#38bdf8',
  retained: '#a78bfa',
  none: '#94a3b8',
};

interface SensorRow {
  sensor: Sensor;
  category: PcdCategory;
}

const PcdStatusModal: React.FC<PcdStatusModalProps> = ({ isOpen, onClose }) => {
  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const sensors = useSceneStore((state: SceneState) => state.sensors);
  const updateCurrentProject = useSceneStore((state: SceneState) => state.updateCurrentProject);
  const setSelectedObjectId = useSceneStore((state: SceneState) => state.setSelectedObjectId);

  const [removingSensorId, setRemovingSensorId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const rows = useMemo<SensorRow[]>(() => {
    const lastUploadAt = currentProject?.lastPcdBundleUploadAt ?? null;
    return sensors
      .map((sensor) => ({
        sensor,
        category: categorizeSensorPcd(sensor, lastUploadAt),
      }))
      .sort((left, right) => left.sensor.name.localeCompare(right.sensor.name));
  }, [currentProject?.lastPcdBundleUploadAt, sensors]);

  const counts = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc[row.category] += 1;
        return acc;
      },
      { new: 0, retained: 0, none: 0 } as Record<PcdCategory, number>
    );
  }, [rows]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    setErrorMessage(null);
    setRemovingSensorId(null);
    onClose();
  };

  const handleRemove = async (sensor: Sensor) => {
    if (!currentProject || !sensor.pcdUrl) {
      return;
    }
    setRemovingSensorId(sensor.id);
    setErrorMessage(null);
    try {
      const project = await deleteAlignmentScan(currentProject.id, sensor.id);
      invalidateBinPointCloudCache(sensor.pcdUrl);
      updateCurrentProject(project);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to remove PCD';
      setErrorMessage(message);
    } finally {
      setRemovingSensorId(null);
    }
  };

  return (
    <div style={overlayStyle} data-testid="pcd-status-modal-overlay" onClick={handleClose}>
      <div
        style={panelStyle}
        data-testid="pcd-status-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>PCD Status</h2>
            <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: '13px' }}>
              {counts.new} new, {counts.retained} retained, {counts.none} without PCD
            </p>
          </div>
          <button type="button" style={secondaryButtonStyle} onClick={handleClose}>
            Close
          </button>
        </div>

        {errorMessage && (
          <div style={{ color: '#f87171', fontSize: '12px', marginBottom: '10px' }} data-testid="pcd-status-error">
            {errorMessage}
          </div>
        )}

        <div style={scrollBodyStyle}>
          {rows.map(({ sensor, category }) => {
            const alignmentStatus = sensor.alignmentStatus || 'none';
            const comparison = sensor.pcdComparison;
            const verdictText = category === 'new' ? formatComparisonVerdict(comparison?.verdict) : '';
            const pcdDateText = formatPcdTimestamp(sensorPcdTimestamp(sensor));

            return (
              <div
                key={sensor.id}
                data-testid={`pcd-status-row-${sensor.id}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(100px, 1.1fr) minmax(80px, 0.7fr) minmax(70px, 0.6fr) minmax(90px, 0.8fr) minmax(120px, 1.2fr) auto',
                  gap: '8px',
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid #334155',
                  fontSize: '12px',
                }}
              >
                <button
                  type="button"
                  onClick={() => setSelectedObjectId(sensor.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: 600,
                    padding: 0,
                  }}
                >
                  {sensor.name}
                </button>
                <span style={{ color: categoryColor[category] }} data-testid={`pcd-status-category-${sensor.id}`}>
                  {pcdCategoryLabel[category]}
                </span>
                <span style={{ color: statusColor(alignmentStatus) }} data-testid={`pcd-status-alignment-${sensor.id}`}>
                  {alignmentStatus}
                </span>
                <span
                  style={{ color: '#94a3b8' }}
                  data-testid={`pcd-status-date-${sensor.id}`}
                  title={sensorPcdTimestamp(sensor) ?? undefined}
                >
                  {pcdDateText}
                </span>
                <span style={{ color: '#cbd5e1' }} data-testid={`pcd-status-metrics-${sensor.id}`}>
                  {comparison ? (
                    <>
                      {Math.round(comparison.inlier_ratio * 100)}% inliers · {comparison.rmse.toFixed(3)} m RMS
                      {verdictText ? ` · ${verdictText}` : ''}
                    </>
                  ) : (
                    <span style={{ color: '#64748b' }}>—</span>
                  )}
                </span>
                {sensor.pcdUrl ? (
                  <button
                    type="button"
                    style={dangerButtonStyle}
                    data-testid={`pcd-status-remove-${sensor.id}`}
                    disabled={removingSensorId === sensor.id}
                    onClick={() => void handleRemove(sensor)}
                  >
                    {removingSensorId === sensor.id ? 'Removing...' : 'Remove'}
                  </button>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PcdStatusModal;
